import { useLocation, useNavigate, type NavigateFunction } from "react-router-dom";
import { useRoutes } from "react-router-dom";
import { useEffect } from "react";
import routes from "./config";
import { DEFAULT_OG_IMAGE, SEO_BY_PATH, SITE_URL, type SeoMeta } from "../seo/metadata";

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

function normalizePath(pathname: string): string {
  if (!pathname || pathname === "/") return "/";
  const cleaned = pathname
    .split("/")
    .map((segment) =>
      segment
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/ı/g, "i")
        .replace(/İ/g, "i")
        .replace(/[^a-zA-Z0-9-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .toLowerCase()
    )
    .filter(Boolean)
    .join("/");
  return `/${cleaned}`;
}

function ensureMeta(name: string, content: string, property = false): void {
  const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let tag = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement("meta");
    if (property) tag.setAttribute("property", name);
    else tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function ensureCanonical(url: string): void {
  let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
}

function ensureJsonLd(jsonLd?: SeoMeta["jsonLd"]): void {
  const scriptId = "dynamic-seo-jsonld";
  const existing = document.getElementById(scriptId);
  if (!jsonLd) {
    existing?.remove();
    return;
  }
  const script = existing ?? document.createElement("script");
  script.id = scriptId;
  script.setAttribute("type", "application/ld+json");
  script.textContent = JSON.stringify(jsonLd);
  if (!existing) document.head.appendChild(script);
}

export function AppRoutes() {
  const element = useRoutes(routes);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(window.REACT_APP_NAVIGATE);
  }, [navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    const normalizedPath = normalizePath(location.pathname);
    if (location.pathname !== normalizedPath) {
      navigate(`${normalizedPath}${location.search}${location.hash}`, { replace: true });
    }
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    const meta = SEO_BY_PATH[location.pathname] ?? SEO_BY_PATH["/404"];
    const canonicalUrl = `${SITE_URL}${meta.path === "/" ? "/" : meta.path}`;

    document.documentElement.setAttribute("lang", "tr");
    document.title = meta.title;
    ensureCanonical(canonicalUrl);
    ensureMeta("description", meta.description);
    ensureMeta("keywords", meta.keywords);
    ensureMeta("robots", meta.robots ?? "index, follow");
    ensureMeta("author", "NiğPak Temizlik");

    ensureMeta("og:title", meta.title, true);
    ensureMeta("og:description", meta.description, true);
    ensureMeta("og:type", meta.type ?? "website", true);
    ensureMeta("og:url", canonicalUrl, true);
    ensureMeta("og:image", DEFAULT_OG_IMAGE, true);
    ensureMeta("og:locale", "tr_TR", true);
    ensureMeta("og:site_name", "NiğPak Temizlik", true);

    ensureMeta("twitter:card", "summary_large_image");
    ensureMeta("twitter:title", meta.title);
    ensureMeta("twitter:description", meta.description);
    ensureMeta("twitter:image", DEFAULT_OG_IMAGE);

    ensureJsonLd(meta.jsonLd);
  }, [location.pathname]);

  return element;
}
